import { v4 as uuid } from 'uuid';
import { useState, useCallback } from 'react';
import { isEmpty, without } from 'lodash';
import { useIntl } from 'react-intl';
import {
  withBlockExtensions,
  getBlocksLayoutFieldname,
} from '@plone/volto/helpers';

import {
  SidebarPortal,
  BlockDataForm,
  BlocksToolbar,
} from '@plone/volto/components';
import View from './View';
import Schema from './schema';

interface EditProps {
  block: string;
  data: any;
  onChangeBlock: (blockId: string, newData: any) => void;
  selected: boolean;
  blocksConfig: any;
  setSidebarTab: (tabIndex: number) => void;
  onFocusNextBlock: (blockId: string, element: HTMLElement) => void;
}

interface BlockDataUpdate {
  blocks: Record<string, any>;
  blocks_layout: {
    items: string[];
  };
  [key: string]: any;
}

const Edit: React.FC<EditProps> = (props) => {
  const {
    block,
    data,
    onChangeBlock,
    selected,
    blocksConfig,
    setSidebarTab,
    onFocusNextBlock,
  } = props;
  const [multiSelected, setMultiSelected] = useState<string[] | null>([]);
  let changed = false;
  if (isEmpty(data?.blocks)) {
    const id = uuid();
    data.blocks = {
      [id]: {
        '@type': 'slate',
        plaintext: '',
        value: [{ children: [{ text: '' }], type: 'p' }],
      },
    };
    data.blocks_layout = {
      items: [id],
    };
    changed = true;
  }

  if (!('block_fullwidth' in data)) {
    data.block_fullwidth = true;
    changed = true;
  }

  if (changed) {
    onChangeBlock(block, { ...data });
  }

  const [selectedBlock, setSelectedBlock] = useState(
    data.blocks_layout?.items?.[0],
  );
  const schema = Schema(useIntl());
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const hasblockActive = !!selectedBlock;

    if (!hasblockActive) {
      return;
    }

    const selectedIndex = data.blocks_layout.items.indexOf(selectedBlock);

    if (e.key === 'Enter') {
      if (!e.shiftKey) {
        const newData = { ...data };
        const id = uuid();
        newData.blocks[id] = {
          '@type': 'slate',
          plaintext: '',
          value: [{ children: [{ text: '' }], type: 'p' }],
        };
        newData.blocks_layout.items = [
          ...data.blocks_layout.items.slice(0, selectedIndex + 1),
          id,
          ...data.blocks_layout.items.slice(selectedIndex + 1),
        ];
        onChangeBlock(block, newData);
        onFocusNextBlock(selectedBlock, e.target as HTMLElement);
        setSelectedBlock(id);
      }
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const onSelectBlock = useCallback(
    (
      id: string | null,
      isMultipleSelection: boolean,
      event:
        | React.MouseEvent<HTMLElement>
        | React.KeyboardEvent<HTMLElement>
        | undefined,
      activeBlock: string | null,
    ) => {
      let newMultiSelected: string[] = [];
      let selected = id;

      if (isMultipleSelection) {
        selected = null;
        const blocksLayoutFieldname = getBlocksLayoutFieldname(data);
        const blocks_layout = data[blocksLayoutFieldname].items;
        if (event?.shiftKey) {
          const anchor =
            multiSelected && multiSelected.length > 0
              ? blocks_layout.indexOf(multiSelected[0])
              : blocks_layout.indexOf(activeBlock);
          const focus = blocks_layout.indexOf(id);
          if (anchor === focus) {
            id ? (newMultiSelected = [id]) : (newMultiSelected = []);
          } else if (focus > anchor) {
            newMultiSelected = [...blocks_layout.slice(anchor, focus + 1)];
          } else {
            newMultiSelected = [...blocks_layout.slice(focus, anchor + 1)];
          }
        }
        if ((event?.ctrlKey || event?.metaKey) && !event?.shiftKey) {
          if (id && multiSelected && multiSelected.includes(id)) {
            selected = null;
            newMultiSelected = without(multiSelected, id);
          } else {
            newMultiSelected = [...(multiSelected || []), id as string];
          }
        }
      }

      setSelectedBlock(selected);
      setMultiSelected(newMultiSelected);
    },
    [data, multiSelected],
  );

  const changeBlockData = (newBlockData: BlockDataUpdate) => {
    let pastedBlocks = newBlockData.blocks_layout.items.filter((blockID) => {
      return !data?.blocks_layout?.items?.find((x: string) => x === blockID);
    });
    const selectedIndex =
      data?.blocks_layout?.items?.indexOf(selectedBlock) + 1;
    const newData = {
      ...data,
      ...newBlockData,
      blocks_layout: {
        items: [
          ...data.blocks_layout.items.slice(0, selectedIndex),
          ...pastedBlocks,
          ...data.blocks_layout.items.slice(selectedIndex),
        ],
      },
    };

    onChangeBlock(block, newData);
  };

  return (
    /* eslint-disable jsx-a11y/no-static-element-interactions */
    <div
      onKeyDown={handleKeyDown}
      // The tabIndex is required for the keyboard navigation
      /* eslint-disable jsx-a11y/no-noninteractive-tabindex */
      tabIndex={-1}
    >
      <button
        className="block__edit"
        onClick={() => {
          setSelectedBlock(null);
          setSidebarTab(1);
        }}
      >
        Abschnitt bearbeiten
      </button>
      <View
        {...props}
        isEdit
        data={data}
        selectedBlock={selectedBlock}
        onSelectBlock={onSelectBlock}
        selected={selected}
        multiSelected={multiSelected}
      />
      {selected ? (
        <BlocksToolbar
          selectedBlock={Object.keys(selectedBlock || {})[0]}
          selectedBlocks={multiSelected}
          onSetSelectedBlocks={(blockIds: string[]) => {
            setMultiSelected(blockIds);
          }}
          formData={data}
          onSelectBlock={(
            id: string,
            l: boolean,
            e:
              | React.MouseEvent<HTMLElement>
              | React.KeyboardEvent<HTMLElement>
              | undefined,
          ) => {
            const isMultipleSelection = e
              ? e.shiftKey || e.ctrlKey || e.metaKey
              : false;
            onSelectBlock(id, isMultipleSelection, e, selectedBlock);
          }}
          onChangeBlocks={(newBlockData: BlockDataUpdate) => {
            changeBlockData(newBlockData);
          }}
        />
      ) : (
        ''
      )}
      <SidebarPortal selected={selected && !selectedBlock}>
        <BlockDataForm
          schema={schema}
          title={schema?.title}
          onChangeField={(id: string, value: any) => {
            onChangeBlock(block, {
              ...data,
              [id]: value,
            });
          }}
          onChangeBlock={onChangeBlock}
          formData={data}
          block={block}
          blocksConfig={blocksConfig}
        />
      </SidebarPortal>
    </div>
  );
};

export default withBlockExtensions(Edit);
